import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { FlashCountdown } from "./ProductCard";
import { SPICE_LEVELS, WEIGHTS, CAKE_SIZES, priceWithOptions } from "../lib/options";
import api from "../lib/api";
import { imgProps } from "../lib/img";
import { trackViewItem } from "../lib/analytics";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

function Pills({ label, values, value, onChange, testPrefix }) {
  return (
    <div>
      <p className="text-xs font-bold text-neutral-600 mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-2">
        {values.map((v) => (
          <button key={v} type="button" data-testid={`${testPrefix}-${v.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            onClick={() => onChange(v)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border-2 transition-colors ${value === v ? "border-[#E04F33] bg-[#E04F33]/5 text-[#E04F33]" : "border-neutral-200 text-neutral-600 hover:border-neutral-300"}`}>
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ItemDetailSheet({ product, businessName, deal, open, onOpenChange }) {
  const { addFood, setDrawerOpen, channel } = useCart();
  const [spice, setSpice] = useState("Medium");
  const [base, setBase] = useState("");
  const [weight, setWeight] = useState("1 lb");
  const [size, setSize] = useState("Small");
  const [persons, setPersons] = useState("");
  const [note, setNote] = useState("");
  const [allergy, setAllergy] = useState("");
  const [qty, setQty] = useState(1);
  const [comboSpices, setComboSpices] = useState({});
  const [addons, setAddons] = useState([]);
  const [pickedAddons, setPickedAddons] = useState([]);

  useEffect(() => {
    setSpice("Medium"); setWeight("1 lb"); setSize("Small"); setPersons(""); setNote(""); setAllergy(""); setQty(1);
    setBase("");
    setComboSpices({}); setPickedAddons([]);
  }, [product?.id]);

  useEffect(() => {
    if (open && product && !product.id.startsWith("exg_")) trackViewItem(product);
  }, [open, product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !product || product.id.startsWith("exg_")) { setAddons([]); return; }
    api.get("/cart/suggestions", { params: { exclude: product.id, channel } })
      .then(({ data }) => setAddons(data.filter((a) => a.id !== product.id).slice(0, 3)))
      .catch(() => setAddons([]));
  }, [open, product?.id, channel]);

  if (!product) return null;
  const kind = product.option_kind;
  const spiceComps = kind === "combo" ? (product.spice_components || []) : [];
  const comboOpts = () => {
    const cs = {};
    spiceComps.forEach((c) => { cs[c.id] = comboSpices[c.id] || "Medium"; });
    if (!Object.keys(cs).length) return {};
    const summary = spiceComps.map((c) => `${c.name.split("(")[0].trim()}: ${cs[c.id]}`).join(", ");
    return { component_spices: cs, spice_summary: summary };
  };
  const options = {
    ...(kind === "spice" ? { spice }
      : kind === "weight" ? { weight }
      : kind === "cake" ? { size, ...(size === "Custom" && persons ? { persons: parseInt(persons, 10) } : {}) }
      : kind === "combo" ? comboOpts()
      : {}),
    ...(product.base_choices && base ? { base } : {}),
  };
  const unit = priceWithOptions(product.display_price, options);
  const regular = priceWithOptions(product.regular_price, options);

  const add = async () => {
    if (!product.in_stock || !product.display_price) { toast.error(`${product.name} is sold out today`); return; }
    if (kind === "cake" && size === "Custom" && (!persons || parseInt(persons, 10) < 1)) {
      toast.error("Enter how many people the custom cake is for"); return;
    }
    if (product.base_choices && !base) { toast.error("Choose your base first"); return; }
    if (!(await addFood(product, qty, { options, note, allergy }))) { onOpenChange(false); return; }
    pickedAddons.forEach((id) => {
      const a = addons.find((x) => x.id === id);
      if (a) addFood(a, 1, {});
    });
    toast.success(`${product.name} added to cart`);
    onOpenChange(false);
    setDrawerOpen(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto p-0 gap-0" data-testid="item-detail-sheet">
        {product.image && (
          <img {...imgProps(product.image, [480, 640], "(min-width: 640px) 448px, 100vw")} alt={product.name} width="448" height="176" className="w-full h-44 object-cover rounded-t-lg" />
        )}
        <div className="p-5 space-y-4">
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="font-display text-xl leading-tight" data-testid="item-detail-name">{product.name}</DialogTitle>
            {businessName && <p className="text-[10px] font-bold uppercase tracking-wide text-[#E04F33]">{businessName}</p>}
            <DialogDescription className="text-sm text-neutral-500" data-testid="item-detail-description">
              {product.description || "Made fresh to order."}
            </DialogDescription>
            {product.dietary_tags?.length > 0 && <div className="flex flex-wrap gap-1" data-testid="item-detail-tags">{product.dietary_tags.map((t) => <span key={t} className="text-[10px] font-bold uppercase tracking-wide bg-green-50 text-green-800 border border-green-200 px-1.5 py-0.5 rounded-full">{t}</span>)}</div>}
            {product.slug && !product.coming_soon && (
              <Link to={`/menu/${product.slug}`} onClick={() => onOpenChange?.(false)} data-testid="item-detail-dish-link"
                className="text-xs font-semibold text-[#E04F33] hover:underline inline-block pt-1">
                Dish page →
              </Link>
            )}
          </DialogHeader>

          {deal && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="item-detail-deal">
              <span className="bg-[#E04F33] text-white text-[11px] font-bold px-2.5 py-1 rounded-full">{deal.label}</span>
              {deal.is_flash && deal.expires_at && <FlashCountdown endsAt={deal.expires_at} />}
            </div>
          )}

          {product.base_choices && (
            <div data-testid="base-choice-section">
              <Pills label="Choose your base (required)" values={product.base_choices} value={base} onChange={setBase} testPrefix="base-option" />
            </div>
          )}
          {kind === "spice" && (
            <Pills label="Spice level" values={SPICE_LEVELS} value={spice} onChange={setSpice} testPrefix="spice-option" />
          )}
          {kind === "combo" && spiceComps.length > 0 && (
            <div className="space-y-3" data-testid="combo-spice-section">
              <p className="text-xs font-bold text-neutral-600">Spice level per dish</p>
              {spiceComps.map((c) => (
                <div key={c.id}>
                  <p className="text-[11px] font-semibold text-neutral-500 mb-1">{c.name}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {SPICE_LEVELS.map((v) => (
                      <button key={v} type="button"
                        data-testid={`combo-spice-${c.id}-${v.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        onClick={() => setComboSpices((prev) => ({ ...prev, [c.id]: v }))}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border-2 transition-colors ${(comboSpices[c.id] || "Medium") === v ? "border-[#E04F33] bg-[#E04F33]/5 text-[#E04F33]" : "border-neutral-200 text-neutral-600 hover:border-neutral-300"}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {kind === "weight" && (
            <Pills label="Weight" values={WEIGHTS} value={weight} onChange={setWeight} testPrefix="weight-option" />
          )}
          {kind === "cake" && (
            <>
              <Pills label="Size" values={CAKE_SIZES} value={size} onChange={setSize} testPrefix="size-option" />
              {size === "Custom" && (
                <div>
                  <p className="text-xs font-bold text-neutral-600 mb-1.5">How many people?</p>
                  <Input data-testid="persons-input" type="number" min="1" max="500" placeholder="e.g. 25"
                    value={persons} onChange={(e) => setPersons(e.target.value)} className="rounded-xl max-w-[140px]" />
                  <p className="text-[11px] text-amber-600 mt-1.5">Custom cakes are quoted per person count — we'll confirm the final price with you.</p>
                </div>
              )}
              {size !== "Custom" && size !== "Small" && (
                <p className="text-[11px] text-neutral-500">{size} adds ${size === "Medium" ? "10.00" : "20.00"} to the listed price.</p>
              )}
            </>
          )}

          <div>
            <p className="text-xs font-bold text-neutral-600 mb-1.5">Special instructions</p>
            <Textarea data-testid="item-note-input" maxLength={120} placeholder="e.g. no cilantro, extra sauce… (optional)"
              value={note} onChange={(e) => setNote(e.target.value)} className="rounded-xl text-sm" rows={2} />
          </div>

          <div className="rounded-xl border border-red-200 bg-red-50/60 p-3" data-testid="item-allergy-block">
            <p className="text-xs font-bold text-red-800 mb-1.5">Allergy for this dish</p>
            <Input data-testid="item-allergy-input" maxLength={200} placeholder="e.g. peanuts, shellfish, dairy (optional)"
              value={allergy} onChange={(e) => setAllergy(e.target.value)} className="rounded-xl text-sm bg-white" />
            <p className="text-[11px] text-red-800/80 mt-1.5">Goes straight to the kitchen ticket for this dish.</p>
          </div>

          {addons.length > 0 && (
            <div data-testid="detail-addons-section">
              <p className="text-xs font-bold text-neutral-600 mb-1.5">Popular add-ons</p>
              <div className="flex flex-wrap gap-2">
                {addons.map((a) => {
                  const picked = pickedAddons.includes(a.id);
                  return (
                    <button key={a.id} type="button" data-testid={`detail-addon-${a.id}`}
                      onClick={() => setPickedAddons((prev) => picked ? prev.filter((x) => x !== a.id) : [...prev, a.id])}
                      className={`inline-flex items-center gap-1.5 pl-2 pr-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${picked ? "border-[#14532D] bg-[#14532D]/5 text-[#14532D]" : "border-neutral-200 text-neutral-600 hover:border-neutral-300"}`}>
                      {picked ? <Check size={12} /> : <Plus size={12} />}
                      {a.name} · ${(a.display_price || 0).toFixed(2)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-3">
              <button data-testid="detail-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-neutral-200"><Minus size={14} /></button>
              <span className="text-base font-bold w-6 text-center" data-testid="detail-qty">{qty}</span>
              <button data-testid="detail-qty-plus" onClick={() => setQty(Math.min(50, qty + 1))}
                className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-neutral-200"><Plus size={14} /></button>
            </div>
            <button data-testid="detail-add-to-cart" onClick={add}
              disabled={!product.in_stock || !product.display_price || product.coming_soon}
              className="px-6 py-3 rounded-full bg-[#E04F33] hover:bg-[#C94228] disabled:bg-neutral-300 text-white text-sm font-bold transition-colors">
              {product.coming_soon ? "Coming soon" : <>Add {qty > 1 ? `${qty} · ` : "· "}${(unit * qty).toFixed(2)}
              {product.on_sale && regular > unit && <s className="ml-1.5 font-normal opacity-70">${(regular * qty).toFixed(2)}</s>}</>}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
